<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="isLoading"
            :query-count="$_queryHandler_numberOfQueries"
            :query-changed="$_queryHandler_hasQueryChanged"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input v-model="query.searchTerm" clearable hide-details />
            </template>
            <template #default>
              <v-row>
                <v-col sm="5">
                  <select-list
                    v-model="query.params.partner"
                    :items="partnerOptions"
                    custom-type="avatar"
                    label="Partner"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col sm="7">
                  <select-list
                    v-model="query.params.status"
                    :items="statusOptions"
                    label="Status"
                    multiple
                    hide-details
                  />
                </v-col>
                <v-col sm="5">
                  <select-list
                    v-model="query.params.type"
                    :items="dealTypeOptions"
                    label="Type"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col sm="7">
                  <select-list
                    v-model="query.params.countries"
                    :items="countriesOptions"
                    custom-type="country"
                    label="Countries"
                    multiple
                    hide-details
                  />
                </v-col>
                <v-col sm="12">
                  <select-list
                    v-model="query.params.offerTypes"
                    :items="offerTypes"
                    label="Offer Types"
                    multiple
                    hide-details
                  />
                </v-col>
              </v-row>
            </template>
            <template #content>
              <deals-table
                :deals="deals"
                editable
                disable-sort
                :server-items-length="pagination.total"
                :items-per-page="pagination.perPage"
                :page="pagination.currentPage"
                :footer-props="{ 'items-per-page-options': [20, 50, 100, -1] }"
                @update:items-per-page="onUpdateItemsPerPage"
                @update:page="onUpdatePage"
                @deal-view="onDealView"
                @deal-renew="onDealRenew"
                @deal-delete="onDealDelete"
              />
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>

    <deal-view-modal v-model="showDealViewModal" :deal="selectedDeal" @modal-cancel="resetSelectedDeal" />

    <fab-actions
      :can-create="isPermittedTo('create')"
      @export="onClickExport"
      @create="$router.push({ name: 'deals.create' })"
    />

    <!-- Renew Modal -->
    <generic-modal
      v-model="showRenewModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Renew Deal"
      hide-louis
      persistent
      @modal-confirm="onRenewConfirm"
      @modal-cancel="resetSelectedDeal"
    >
      <div>
        Are you sure you want to renew this deal (<span class="font-weight-bold"
          >{{ getPartnerNameBySlug(selectedDeal.partner) }} - {{ selectedDeal.name }}</span
        >)?
      </div>
      <div class="mt-4">
        Confirming will end this deal and redirect to the 'Create New Deal' page, passing this deal's data as a
        foundation.
      </div>
    </generic-modal>

    <!-- Delete Modal -->
    <generic-modal
      v-model="showDeleteModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Delete Deal"
      confirm-color="error"
      confirm-text="Delete"
      @modal-confirm="onDeleteConfirm"
      @modal-cancel="resetSelectedDeal"
    >
      <div class="mb-2 red--text font-weight-bold">
        {{ getPartnerNameBySlug(selectedDeal.partner) }} - {{ selectedDeal.name }}
      </div>
      <div>
        Are you sure you want to <span class="red--text font-weight-bold">DELETE</span> this deal? This cannot be
        undone.
      </div>
    </generic-modal>
  </page-loader>
</template>

<script>
// Helpers
import { mapGetters, mapMutations } from 'vuex'
import { merge } from 'lodash-es'
import { formatDate } from '@/utils/date'
// Components
import FabActions from '@/components/fab-actions'
import DealsTable from './components/deals-table'
import DealViewModal from './components/deal-view-modal'
import FilterAccordion from '@/components/filter-accordion'
import GenericModal from '@/components/modals/generic-modal'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'
// Mixins
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'
// Services
import { deals, partners, partnerReports } from '@/services/offers'
const dealModel = ({
  commission_rates = {},
  conversion_rate_guarantee = {},
  countries = [],
  currency = null,
  end = null,
  flat_fee = [],
  offer_types = [],
  name = '',
  notes = null,
  partner = null,
  payment_type = null,
  start = null,
  status = null,
  tags = [],
  type = null,
} = {}) => ({
  commission_rates,
  conversion_rate_guarantee,
  countries,
  currency,
  end,
  flat_fee,
  offer_types,
  name,
  notes,
  partner,
  payment_type,
  start,
  status,
  tags,
  type,
})
const transform = ({ start, end, ...rest }) => ({
  start: formatDate(start, { separator: '-' }) || null,
  end: formatDate(end, { separator: '-' }) || null,
  ...rest,
})
export default {
  name: 'DealsManagement',
  components: {
    FabActions,
    DealsTable,
    DealViewModal,
    FilterAccordion,
    GenericModal,
    PageLoader,
    SearchInput,
    SelectList,
  },
  mixins: [PermissionsHandler, QueryParamsHandler],
  data: () => ({
    isLoading: false,
    defaultQuery: {
      status: ['live'],
      countries: [],
      offerTypes: [],
    },
    deals: [],
    partners: [],
    pagination: {
      perPage: 20,
    },
    queriesDefined: ['partner', 'status', 'type', 'countries', 'offerTypes'],
    queryDefaults: ['status'],
    queryTypes: { countries: 'array', status: 'array', offerTypes: 'array' },
    selectedDeal: {},
    showDealViewModal: false,
    showDeleteModal: false,
    showRenewModal: false,
  }),
  computed: {
    ...mapGetters({ getItemsForOption: 'options/getItemsForOption', getStatusOptions: 'deals/getStatusOptions' }),
    countriesOptions() {
      return this.getItemsForOption('countries')
    },
    dealTypeOptions() {
      return this.getItemsForOption('dealTypes')
    },
    offerTypes() {
      return this.getItemsForOption('offerTypes')
    },
    partnerOptions() {
      return this.partners.map(({ name, slug, logoThumbnail }) => ({ key: name, value: slug, icon: logoThumbnail }))
    },
    statusOptions() {
      return this.getStatusOptions()
    },
  },
  async created() {
    await Promise.all([this.getDeals(), this.getPartners()])
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async getDeals() {
      this.isLoading = true
      const { searchTerm: search } = this.query
      const filters = this.getFormattedFilters()
      const { perPage, currentPage: page } = this.pagination
      try {
        const {
          data,
          pagination: { total },
        } = await deals.getFilteredDeals({
          filters,
          search,
          perPage: perPage === -1 ? null : perPage,
          page: perPage === -1 ? 1 : page,
        })
        this.pagination.total = total
        this.deals = data
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve deals',
          data: { error },
        })
      }
      this.isLoading = false
    },
    getFormattedFilters() {
      const {
        params: { status: statusArray, countries: countriesArray, offerTypes: offerTypesArray, ...params },
      } = this.query
      const status = statusArray && statusArray.length ? { statusIn: statusArray } : {}
      const countries = countriesArray && countriesArray.length ? { countriesContains: countriesArray } : {}
      const offerTypes = offerTypesArray && offerTypesArray.length ? { offer_typesContains: offerTypesArray } : {}
      return { ...status, ...countries, ...offerTypes, ...params }
    },
    async getPartners() {
      this.isLoading = true
      try {
        const { data } = await partners.getAllPartners({ compact: true, filters: { archivedContains: [true, false] } })
        this.partners = data
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve partners',
          data: { error },
        })
      }
      this.isLoading = false
    },
    getPartnerNameBySlug(slug) {
      return this.partners.find(partner => partner.slug === slug)?.name || slug
    },
    async handleQueryUpdate() {
      await this.getDeals()
    },
    onDealDelete(deal) {
      this.selectedDeal = deal
      this.showDeleteModal = true
    },
    onDealView(deal) {
      this.selectedDeal = deal
      this.showDealViewModal = true
    },
    onDealRenew(deal) {
      this.selectedDeal = deal
      this.showRenewModal = true
    },
    async onUpdateItemsPerPage(perPage) {
      this.pagination.perPage = perPage
      await this.onUpdatePage(1)
    },
    async onUpdatePage(page) {
      this.pagination.currentPage = page
      await this.handleQueryUpdate()
    },
    async onClickExport() {
      this.isLoading = true
      const { searchTerm: search } = this.query
      const filters = this.getFormattedFilters()
      try {
        await deals.exportCsv({ filters, search })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to export to CSV',
        })
      }
      this.isLoading = false
    },
    async onDeleteConfirm() {
      this.isLoading = true
      try {
        await deals.deleteDeal(this.selectedDeal)
        this.$notify({
          type: 'success',
          text: `Successfully removed deal '${this.selectedDeal.name}'`,
        })
        this.showDeleteModal = false
        this.resetSelectedDeal()
        await this.getDeals()
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete deal',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    async onRenewConfirm() {
      this.isLoading = true
      try {
        const linkedPartnerReports = await this.fetchPartnerReportsByDeal()
        const endedDeal = dealModel(
          merge({}, this.selectedDeal, { end: formatDate(new Date(), { separator: '-' }), status: 'expired' })
        )
        const newDeal = dealModel(merge({}, this.selectedDeal, { end: null, start: null, status: null }))
        await deals.updateDeal(this.selectedDeal.id, transform(endedDeal))
        this.$notify({
          type: 'success',
          text: 'Successfully ended deal',
        })
        this.$router.push({ name: 'deals.create', params: { renewal: newDeal, linkedPartnerReports } })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to renew deal',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    async fetchPartnerReportsByDeal() {
      try {
        const { data } = await partnerReports.getFilteredReports({
          filters: { deal_id: this.selectedDeal.id },
        })
        return data || []
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch linked partner reports',
          data: { error },
          duration: -1,
        })
        return []
      }
    },
    resetSelectedDeal() {
      this.selectedDeal = {}
    },
  },
}
</script>
